import { Dispatch } from 'redux'

import * as ApiV4 from 'redux/api/DataPlatform/dataProducts/v4'
import * as ApiV3 from 'redux/api/Applications/DataProducts/v3'
import { useThunkActions } from 'redux/actions/useThunkActions'
import { toggleSnackbarOpen } from 'redux/actions/Snackbar'
import { SnackbarMessage } from 'types/GlobalSnackbar'
import { parseApiError } from 'utils/errorParser'

import * as actions from './actions'

function tryGetSIM(type: 'custom' | 'standard' = 'standard') {
  return (dispatch: Dispatch) => {
    dispatch(actions.getSIM())

    ApiV4.getSIM(type)
      .then((response) => {
        dispatch(actions.getSIMSuccess(response.data.data))
      })
      .catch((error) => {
        dispatch(
          actions.getSIMFailed({ notFound: error?.response?.status === 404 })
        )
      })
  }
}

async function tryGetSIMSchema(): Promise<ApiV4.SIMSchemaModel[]> {
  return ApiV4.getSIMSchema().then((response) => response.data.data)
}

/**
 * Trigger the standard information model's import job. The underlying job run
 * still goes through the v3 data-products endpoint, but the resulting run
 * state is stored on the SIM so the overview stays on a single source.
 */
function tryStartSIMImport(id: string) {
  return (dispatch: Dispatch) => {
    dispatch(actions.startSIMImport())

    ApiV3.startJobRun(id)
      .then((response) => {
        dispatch(actions.startSIMImportSuccess(response.data.data))
        dispatch(toggleSnackbarOpen(SnackbarMessage.Success))
      })
      .catch(() => {
        dispatch(actions.startSIMImportFailed())
        dispatch(toggleSnackbarOpen(SnackbarMessage.Failed))
      })
  }
}

function tryPollSIMImport(id: string) {
  return (dispatch: Dispatch) => {
    ApiV3.pollJobRun(id).then((response) => {
      dispatch(actions.pollSIMImportSuccess(response.data.data))
    })
  }
}

/**
 * Delete the whole custom information model. The callback gets the backend's
 * reason when the delete is refused (e.g. the model is still in use).
 */
function tryDeleteCustomSIM(cb: (err?: string) => void) {
  return (dispatch: Dispatch) => {
    ApiV4.deleteCustomSIM()
      .then(() => {
        dispatch(actions.deleteCustomSIMSuccess())
        dispatch(toggleSnackbarOpen(SnackbarMessage.Success))

        cb()
      })
      .catch((error) => {
        cb(parseApiError(error).detail ?? 'error')
      })
  }
}

export const useSIMActions = () => ({
  tryGetSIM: useThunkActions(tryGetSIM),
  tryGetSIMSchema,
  tryDeleteCustomSIM: useThunkActions(tryDeleteCustomSIM),
  tryStartSIMImport: useThunkActions(tryStartSIMImport),
  tryPollSIMImport: useThunkActions(tryPollSIMImport)
})
